import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';
import useLocale from '../hooks/useLocale';
import useUpdateBreadcrumbs from '../hooks/useUpdateBreadcrumbs';
import './ThesisReviewTaskAssignment.css';

const ThesisReviewTaskAssignment = () => {
  // was { thesis_id, conference_id }
  const { festschrift_id, article_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const { getCurrentLanguage, getLocalizedFullName, getLocalizedName } = useLocale();
  const lang = getCurrentLanguage();

  const [taskData, setTaskData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Модалки
  const [showAgreeModal, setShowAgreeModal] = useState(false);
  const [showRefuseModal, setShowRefuseModal] = useState(false);
  const [refusalReason, setRefusalReason] = useState('');

  // Хлебные крошки: дашборд → конкретная статья
  const resetBreadcrumbs = useCallback((search) => {
    const sp = new URLSearchParams(search);
    const confName = sp.get('conferenceName') || '';
    const thesisTitle = sp.get('thesisTitle') || '';
    return [{ label: confName, url: '/dashboard' }, { label: thesisTitle }];
  }, []);
  useUpdateBreadcrumbs(resetBreadcrumbs);

  // Загрузка данных задачи рецензирования
  useEffect(() => {
    const fetchTaskData = async () => {
      setLoading(true);
      try {
        // ID — теперь article_id
        const response = await api.get(`/reviews/task/${article_id}`);
        setTaskData(response.data);
        setError('');
      } catch (err) {
        console.error(err);
        setError(
          lang === 'ru'
            ? 'Ошибка при загрузке данных статьи для рецензирования'
            : 'Failed to load article review task data',
        );
      } finally {
        setLoading(false);
      }
    };
    if (article_id) {
      fetchTaskData();
    }
  }, [article_id, lang]);

  // Обработчики модалок
  const handleAgree = () => setShowAgreeModal(true);
  const handleRefuse = () => setShowRefuseModal(true);
  const cancelAgree = () => setShowAgreeModal(false);
  const cancelRefusal = () => setShowRefuseModal(false);

  // Подтверждение согласия
  const confirmAgree = async () => {
    setShowAgreeModal(false);
    try {
      // festschrift_id вместо conference_id
      const response = await api.post(
        `/reviews/task/${article_id}/accept?festschrift_id=${festschrift_id}`,
      );
      const review_id = response.data.review_id;

      const confName = searchParams.get('conferenceName') || '';
      const thesisTitle = searchParams.get('thesisTitle') || '';

      navigate(
        `/publishing/${festschrift_id}/review/${review_id}` +
          `?conferenceName=${encodeURIComponent(confName)}` +
          `&thesisTitle=${encodeURIComponent(thesisTitle)}` +
          `&editable=True&flow=dashboard`,
      );
    } catch (err) {
      console.error('Ошибка при принятии задачи:', err);
      alert(
        lang === 'ru' ? 'Не удалось принять задачу рецензирования' : 'Failed to accept review task',
      );
    }
  };

  // Отправка причины отказа
  const submitRefusal = async () => {
    if (!refusalReason.trim()) {
      alert(
        lang === 'ru'
          ? 'Пожалуйста, укажите причину отказа'
          : 'Please specify the reason for refusal',
      );
      return;
    }
    try {
      await api.post(`/reviews/task/${article_id}/refuse`, {
        reason: refusalReason,
      });
      setShowRefuseModal(false);
      navigate('/dashboard');
    } catch (err) {
      console.error('Ошибка при отправке причины отказа:', err);
      alert(
        lang === 'ru' ? 'Ошибка при отправке причины отказа' : 'Error submitting refusal reason',
      );
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        {lang === 'ru' ? 'Загрузка данных задачи рецензирования...' : 'Loading review task data...'}
      </div>
    );
  }

  if (error) {
    return <div className="error-state">{error}</div>;
  }

  const { author, title, abstract, text } = taskData || {};
  const authorFullName = author ? getLocalizedFullName(author.full_name) : '';
  const isAuthorAnonymous = !authorFullName && !author?.email;

  const articleTitle = title ? getLocalizedName(title) : '';
  const abstractObj = abstract || text || {};
  const articleAbstract =
    abstractObj && typeof abstractObj === 'object'
      ? abstractObj[lang] || abstractObj.en || abstractObj.ru || ''
      : '';

  return (
    <div className="thesis-review-task-assignment">
      <h1 className="page-title">{lang === 'ru' ? 'Задача рецензирования' : 'Review task'}</h1>

      <div className="content-container">
        <div className="author-card">
          <h2>{lang === 'ru' ? 'Информация о статье' : 'Submission info'}</h2>

          <p>
            <strong>{lang === 'ru' ? 'Название:' : 'Title:'}</strong> {articleTitle}
          </p>

          <p>
            <strong>{lang === 'ru' ? 'Аннотация:' : 'Abstract:'}</strong>{' '}
            {articleAbstract || (lang === 'ru' ? '—' : '—')}
          </p>
        </div>

        <div className="author-card">
          <h2>{lang === 'ru' ? 'Информация об авторе' : 'Author information'}</h2>

          {isAuthorAnonymous ? (
            <p>
              {lang === 'ru'
                ? 'Автор скрыт (анонимное рецензирование)'
                : 'Author is hidden (anonymous review)'}
            </p>
          ) : (
            <>
              <p>
                <strong>{lang === 'ru' ? 'ФИО:' : 'Full name:'}</strong> {authorFullName}
              </p>
              <p>
                <strong>Email:</strong> {author?.email}
              </p>
              {author?.phone && (
                <p>
                  <strong>{lang === 'ru' ? 'Телефон:' : 'Phone:'}</strong> {author.phone}
                </p>
              )}
              {author?.orcid && (
                <p>
                  <strong>ORCID:</strong> {author.orcid}
                </p>
              )}
              {author?.website && (
                <p>
                  <strong>{lang === 'ru' ? 'Веб-сайт:' : 'Website:'}</strong>{' '}
                  <a href={author.website} target="_blank" rel="noopener noreferrer">
                    {author.website}
                  </a>
                </p>
              )}
            </>
          )}
        </div>

        <div className="action-buttons">
          <button className="agree-btn" onClick={handleAgree}>
            {lang === 'ru' ? 'Согласиться рецензировать' : 'Agree to review'}
          </button>
          <button className="refuse-btn" onClick={handleRefuse}>
            {lang === 'ru' ? 'Отказаться от рецензирования' : 'Refuse to review'}
          </button>
        </div>
      </div>

      {showAgreeModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{lang === 'ru' ? 'Подтверждение согласия' : 'Confirm agreement'}</h3>
            <p>
              {lang === 'ru'
                ? 'Вы действительно хотите начать рецензирование этой статьи?'
                : 'Do you really want to start reviewing this article?'}
            </p>
            <div className="modal-actions">
              <button className="confirm-btn" onClick={confirmAgree}>
                {lang === 'ru' ? 'Да' : 'Yes'}
              </button>
              <button className="cancel-btn" onClick={cancelAgree}>
                {lang === 'ru' ? 'Нет' : 'No'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRefuseModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{lang === 'ru' ? 'Отказ от рецензирования' : 'Refuse to review'}</h3>
            <p>
              {lang === 'ru'
                ? 'Пожалуйста, укажите причину отказа:'
                : 'Please specify the reason for refusal:'}
            </p>
            <textarea
              className="refusal-textarea"
              value={refusalReason}
              onChange={(e) => setRefusalReason(e.target.value)}
              placeholder={
                lang === 'ru' ? 'Введите причину отказа...' : 'Enter the reason for refusal...'
              }
            />
            <div className="modal-actions">
              <button className="confirm-btn" onClick={submitRefusal}>
                {lang === 'ru' ? 'Отправить' : 'Submit'}
              </button>
              <button className="cancel-btn" onClick={cancelRefusal}>
                {lang === 'ru' ? 'Отмена' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThesisReviewTaskAssignment;
